import { useState, useCallback } from "react";
import {
  formatHeight,
  stripPhoneFormat,
  stripHandlePrefix,
} from "../../../utils/formatters";
import type {
  PlayerFormErrors,
  PlayerFormParent,
  PlayerFormSetters,
  PlayerFormState,
} from "./playerFormTypes";

const CURRENT_YEAR = new Date().getFullYear();

export const emptyParent = (): PlayerFormParent => ({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  xHandle: "",
  slackUsername: "",
});

const emptyState = (): PlayerFormState => ({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  xHandle: "",
  slackUsername: "",
  instagramUrl: "",
  videoLink: "",
  scoutingProfileLink: "",
  position: "",
  bats: "",
  throws: "",
  heightFeet: "",
  heightInches: "",
  weight: "",
  school: "",
  gradYear: String(CURRENT_YEAR + 4),
  city: "",
  state: "",
  gpa: "",
  intendedMajor: "",
  coachName: "",
  coachPhone: "",
  schoolTwitter: "",
  travelTeam: "",
  travelTeamCoachName: "",
  travelTeamCoachPhone: "",
  travelTeamTwitter: "",
  preferredLocations: [],
  endGoal: "",
  recruitmentStage: "",
  projectedDivisions: [],
  parents: [],
  parentDraft: null,
  notes: "",
});

export interface UsePlayerFormStateResult {
  state: PlayerFormState;
  setters: PlayerFormSetters;
  errors: PlayerFormErrors;
  setErrors: React.Dispatch<React.SetStateAction<PlayerFormErrors>>;
  validate: () => boolean;
  buildPayload: () => PlayerFormPayload;
  reset: () => void;
}

/**
 * JSON-shape sent to backend. Field names match both PlayerSubmissionRequest
 * (intake) and the AddContactModal CRM contact create payload — neither
 * endpoint requires a field beyond what the other ignores.
 */
export interface PlayerFormPayload {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  xHandle?: string;
  slackUsername?: string;
  instagramUrl?: string;
  position: string;
  gradYear: number;
  bats?: string;
  throws?: string;
  height?: string;
  weight?: number;
  videoLink?: string;
  scoutingProfileLink?: string;
  school?: string;
  city?: string;
  state?: string;
  gpa?: string;
  intendedMajor?: string;
  coachName?: string;
  coachPhone?: string;
  schoolTwitter?: string;
  preferredLocations?: string[];
  travelTeam?: string;
  travelTeamCoachName?: string;
  travelTeamCoachPhone?: string;
  travelTeamTwitter?: string;
  recruitmentStage?: string;
  projectedDivisions?: string[];
  endGoal?: string;
  notes?: string;
  videoLinks: string[];
  parent1?: PlayerFormParentPayload;
  parent2?: PlayerFormParentPayload;
}

interface PlayerFormParentPayload {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  slackUsername?: string;
  xHandle?: string;
}

const trimOrUndefined = (v: string): string | undefined => {
  const t = v.trim();
  return t === "" ? undefined : t;
};

const handleOrUndefined = (v: string): string | undefined => {
  const t = stripHandlePrefix(v).trim();
  return t === "" ? undefined : t;
};

export const usePlayerFormState = (): UsePlayerFormStateResult => {
  const [state, setState] = useState<PlayerFormState>(emptyState);
  const [errors, setErrors] = useState<PlayerFormErrors>({});

  const set = useCallback(
    <K extends keyof PlayerFormState>(field: K, value: PlayerFormState[K]) => {
      setState((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const setParents = useCallback((parents: PlayerFormParent[]) => {
    setState((prev) => ({ ...prev, parents }));
  }, []);

  const setParentDraft = useCallback((draft: PlayerFormParent | null) => {
    setState((prev) => ({ ...prev, parentDraft: draft }));
  }, []);

  const updateParentDraft = useCallback(
    (field: keyof PlayerFormParent, value: string) => {
      setState((prev) =>
        prev.parentDraft
          ? { ...prev, parentDraft: { ...prev.parentDraft, [field]: value } }
          : prev
      );
    },
    []
  );

  const togglePreferredLocation = useCallback((s: string) => {
    setState((prev) => {
      const has = prev.preferredLocations.includes(s);
      const next = has
        ? prev.preferredLocations.filter((x) => x !== s)
        : prev.preferredLocations.length < 3
          ? [...prev.preferredLocations, s]
          : prev.preferredLocations;
      return { ...prev, preferredLocations: next };
    });
    setErrors((prev) =>
      prev.preferredLocations ? { ...prev, preferredLocations: undefined } : prev
    );
  }, []);

  const clearError = useCallback((field: string) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  }, []);

  const validate = useCallback((): boolean => {
    const e: PlayerFormErrors = {};
    if (!state.firstName.trim()) e.firstName = "Required";
    if (!state.lastName.trim()) e.lastName = "Required";
    if (!state.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email.trim()))
      e.email = "Invalid email";
    if (!state.phone.trim()) e.phone = "Required";
    else if (stripPhoneFormat(state.phone).length !== 10)
      e.phone = "Must be 10 digits";
    if (!state.position) e.position = "Required";
    if (!state.bats) e.bats = "Required";
    if (!state.throws) e.throws = "Required";
    if (!state.heightFeet.trim()) e.heightFeet = "Required";
    if (!state.weight.trim()) e.weight = "Required";
    if (!state.school.trim()) e.school = "Required";
    if (!state.city.trim()) e.city = "Required";
    if (!state.state) e.state = "Required";
    if (!state.gradYear.trim()) e.gradYear = "Required";
    if (state.preferredLocations.length === 0)
      e.preferredLocations = "Select at least 1 state";
    setErrors(e);
    return Object.values(e).every((v) => !v);
  }, [state]);

  const buildPayload = useCallback((): PlayerFormPayload => {
    const buildParent = (
      p: PlayerFormParent | undefined
    ): PlayerFormParentPayload | undefined => {
      if (!p || !p.firstName.trim()) return undefined;
      return {
        firstName: p.firstName.trim(),
        lastName: p.lastName.trim(),
        email: trimOrUndefined(p.email),
        phone: stripPhoneFormat(p.phone) || undefined,
        slackUsername: trimOrUndefined(p.slackUsername),
        xHandle: handleOrUndefined(p.xHandle),
      };
    };

    return {
      firstName: state.firstName.trim(),
      lastName: state.lastName.trim(),
      email: trimOrUndefined(state.email),
      phone: stripPhoneFormat(state.phone) || undefined,
      xHandle: handleOrUndefined(state.xHandle),
      slackUsername: trimOrUndefined(state.slackUsername),
      instagramUrl: handleOrUndefined(state.instagramUrl),
      position: state.position,
      gradYear: parseInt(state.gradYear, 10),
      bats: state.bats || undefined,
      throws: state.throws || undefined,
      height: formatHeight(state.heightFeet, state.heightInches) || undefined,
      weight: state.weight ? parseInt(state.weight, 10) : undefined,
      videoLink: trimOrUndefined(state.videoLink),
      scoutingProfileLink: trimOrUndefined(state.scoutingProfileLink),
      school: trimOrUndefined(state.school),
      city: trimOrUndefined(state.city),
      state: state.state || undefined,
      gpa: trimOrUndefined(state.gpa),
      intendedMajor: trimOrUndefined(state.intendedMajor),
      coachName: trimOrUndefined(state.coachName),
      coachPhone: stripPhoneFormat(state.coachPhone) || undefined,
      schoolTwitter: handleOrUndefined(state.schoolTwitter),
      preferredLocations:
        state.preferredLocations.length > 0
          ? state.preferredLocations
          : undefined,
      travelTeam: trimOrUndefined(state.travelTeam),
      travelTeamCoachName: trimOrUndefined(state.travelTeamCoachName),
      travelTeamCoachPhone:
        stripPhoneFormat(state.travelTeamCoachPhone) || undefined,
      travelTeamTwitter: handleOrUndefined(state.travelTeamTwitter),
      recruitmentStage: state.recruitmentStage || undefined,
      projectedDivisions:
        state.projectedDivisions.length > 0 ? state.projectedDivisions : undefined,
      endGoal: trimOrUndefined(state.endGoal),
      notes: trimOrUndefined(state.notes),
      videoLinks: [],
      parent1: buildParent(state.parents[0]),
      parent2: buildParent(state.parents[1]),
    };
  }, [state]);

  const reset = useCallback(() => {
    setState(emptyState());
    setErrors({});
  }, []);

  return {
    state,
    setters: {
      set,
      setParents,
      setParentDraft,
      updateParentDraft,
      togglePreferredLocation,
      clearError,
    },
    errors,
    setErrors,
    validate,
    buildPayload,
    reset,
  };
};
